import type { AxiosError } from "axios";
import axios, { AxiosRequestConfig } from "axios";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import * as Sentry from "@sentry/react";
import queryString from "qs";
import { axiosRetry } from "@/lib/cjs-interop";
import { isNotLoginRequired } from "@/helpers/auth";

export const axiosWithRetryInstance = axios.create({
  paramsSerializer: params => queryString.stringify(params, { arrayFormat: "repeat" }),
});

axiosRetry(axiosWithRetryInstance, {
  retries: 3,
  onRetry: (retryCount, err, requestConfig) => {
    Sentry.captureMessage(`Retry attempt #${retryCount} (${requestConfig.method} ${requestConfig.url})`);
    Sentry.captureException(err);
  },
});

export const multiPartHeaders = {
  "Content-Type": "multipart/form-data",
};

export interface FastApiErrorResponse {
  status: number;
  data: {
    data: string[];
    err_type: "NoDocumentsToProcessError";
  };
}

export const apiInstance = axios.create({
  baseURL: "",
  headers: { "X-Requested-With": "XMLHttpRequest" },
  responseType: "json",
  paramsSerializer: params => queryString.stringify(params, { arrayFormat: "repeat" }),
});

apiInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      if (!isNotLoginRequired(document.location.pathname)) {
        const state = btoa(document.location.pathname);
        const nextUrl = `/login?state=${state}`;
        document.location.href = `/pf-auth/logout?next_url=${encodeURIComponent(nextUrl)}`;
      }
    }
    return Promise.reject(error);
  },
);

export const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: "" },
  ): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig["method"];
      data?: AxiosRequestConfig["data"];
      params?: AxiosRequestConfig["params"];
      headers?: AxiosRequestConfig["headers"];
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params, headers }) => {
    try {
      const result = await apiInstance({
        url: baseUrl + url,
        method,
        data,
        params,
        headers,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export interface ListResponse<T> {
  total: number;
  offset: number;
  results: T[];
}
